// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Transport session. Wraps trystero (nostr strategy) into typed actions for the
 * whiteboard protocol. trystero discovers peers over public nostr relays and
 * encrypts the signalling handshake with the room passphrase, so a relay only
 * sees ciphertext. It builds a FULL MESH — every browser connects directly to
 * every other — so ops ride direct WebRTC data channels (DTLS on the wire) with
 * no server ever in the drawing path.
 *
 * Four actions:
 *   hello  — announce this participant's display name (JSON), sent to a new peer
 *            on join and re-broadcast on rename
 *   op     — one encrypted CRDT op frame: [iv | ciphertext] (binary, broadcast)
 *   sync   — the full board as an encrypted op-log bundle: [iv | ciphertext]
 *            (binary), sent targeted to a newly-joined peer so their board matches
 *   cursor — a transient live cursor position { x, y } in logical board coords
 *            (JSON, broadcast, throttled) — a presence signal like a typing dot
 */
import { joinRoom, selfId } from 'trystero/nostr';
import type { Room, BaseRoomConfig, JsonValue, MessageAction } from '@trystero-p2p/core';

const APP_ID = 'sketchwell-v1-benrichardson-dev';

export interface Hello {
  name: string;
}

export interface SessionHandlers {
  onPeerJoin: (peerId: string) => void;
  onPeerLeave: (peerId: string) => void;
  onHello: (peerId: string, hello: Hello) => void;
  onOp: (peerId: string, frame: Uint8Array) => void;
  onSync: (peerId: string, frame: Uint8Array) => void;
  onCursor: (peerId: string, x: number, y: number) => void;
  onError: (message: string) => void;
}

export class BoardSession {
  readonly selfId = selfId;
  private room: Room | null = null;
  private helloA: MessageAction<JsonValue> | null = null;
  private opA: MessageAction<Uint8Array> | null = null;
  private syncA: MessageAction<Uint8Array> | null = null;
  private cursorA: MessageAction<JsonValue> | null = null;

  constructor(
    private roomId: string,
    private pass: string,
    private handlers: SessionHandlers,
  ) {}

  /** Join the room and wire the actions. Returns false if joining threw. */
  join(): boolean {
    const config: BaseRoomConfig = { appId: APP_ID };
    if (this.pass) config.password = this.pass;
    let room: Room;
    try {
      room = joinRoom(config, this.roomId);
    } catch (e) {
      this.handlers.onError(`Could not join: ${(e as Error).message}`);
      return false;
    }
    this.room = room;

    this.helloA = room.makeAction<JsonValue>('hello');
    this.opA = room.makeAction<Uint8Array>('op');
    this.syncA = room.makeAction<Uint8Array>('sync');
    this.cursorA = room.makeAction<JsonValue>('cursor');

    this.helloA.onMessage = (data, ctx) => this.handlers.onHello(ctx.peerId, data as unknown as Hello);
    this.opA.onMessage = (data, ctx) => this.handlers.onOp(ctx.peerId, data as Uint8Array);
    this.syncA.onMessage = (data, ctx) => this.handlers.onSync(ctx.peerId, data as Uint8Array);
    this.cursorA.onMessage = (data, ctx) => {
      const c = data as unknown as { x?: number; y?: number };
      if (typeof c?.x === 'number' && typeof c?.y === 'number') this.handlers.onCursor(ctx.peerId, c.x, c.y);
    };

    room.onPeerJoin = (peerId) => this.handlers.onPeerJoin(peerId);
    room.onPeerLeave = (peerId) => this.handlers.onPeerLeave(peerId);
    return true;
  }

  /** Announce our name. Targeted to a specific peer, or broadcast to all. */
  sendHello(hello: Hello, target?: string): void {
    this.helloA?.send(hello as unknown as JsonValue, target ? { target } : undefined).catch(() => undefined);
  }

  /** Broadcast one encrypted op frame to every peer. */
  broadcastOp(frame: Uint8Array): void {
    this.opA?.send(frame).catch(() => undefined);
  }

  /** Send the full-board sync bundle to one specific peer. */
  sendSync(frame: Uint8Array, target: string): void {
    this.syncA?.send(frame, { target }).catch(() => undefined);
  }

  /** Broadcast our live cursor position (logical coords). */
  sendCursor(x: number, y: number): void {
    this.cursorA?.send({ x, y } as unknown as JsonValue).catch(() => undefined);
  }

  getPeerIds(): string[] {
    return this.room ? Object.keys(this.room.getPeers()) : [];
  }

  leave(): void {
    if (!this.room) return;
    try {
      void this.room.leave();
    } catch {
      /* already left */
    }
    this.room = null;
  }
}
